import {
  sanitizeBrokeredRequest,
  type BrokeredFetchInit,
  type BrokeredFetchResponse,
  type FetchBroker,
} from "@roqeet/core";

/**
 * 중개 fetch 브로커 — 결정 #17. 스크립트는 raw fetch를 절대 받지 못하고, 이
 * 브로커만 통한다. 브로커는:
 *  - core `sanitizeBrokeredRequest`로 요청을 게이팅·정화(allowlist + 자격증명/키
 *    헤더 제거 + SSRF 방어 + 메서드 화이트리스트). 통과 못 하면 throw.
 *  - 정화된 요청만 실제 fetch한다(자격증명 미포함 — credentials: "omit").
 *
 * CORS 프록시 경유(결정 #7)는 후속: 지금은 직접 호출만(allowlist가 CORS 허용
 * 도메인이라는 전제). 응답 본문은 텍스트/ JSON으로만 노출(blob 등 차단).
 */
export class WebFetchBroker implements FetchBroker {
  async request(
    allowlist: string[],
    input: string,
    init?: BrokeredFetchInit,
  ): Promise<BrokeredFetchResponse> {
    // 1) core 정책으로 정화. 거부되면 여기서 throw(우회 불가).
    const safe = sanitizeBrokeredRequest(input, init, allowlist);

    // 2) 정화된 요청만 실제 호출. ambient 자격증명/쿠키 제거(credentials: omit).
    const reqInit: RequestInit = {
      method: safe.method,
      headers: safe.headers,
      credentials: "omit",
      redirect: "follow",
    };
    if (safe.body !== undefined) reqInit.body = safe.body;

    const res = await fetch(safe.url, reqInit);

    // 3) 응답을 안전한 표면으로만 노출(text/json). 본문을 한 번만 읽도록 캐시.
    let cached: string | undefined;
    const readText = async (): Promise<string> => {
      if (cached === undefined) cached = await res.text();
      return cached;
    };

    return {
      ok: res.ok,
      status: res.status,
      text: readText,
      async json() {
        return JSON.parse(await readText());
      },
    };
  }
}
